import * as React from "react"

import { Check, Circle } from "lucide-react"

import { cn } from "../../cn"

export interface CompletionIndicatorProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  /** Whether the step this indicator tracks has been completed. */
  complete: boolean
  /** Names the step, e.g. "Agent". Used to build the accessible label. */
  label: string
}

/**
 * A step's done/not-done state, drawn as a disc at the edge of the step's
 * heading. Both states draw the same `Circle` so their outer edge matches
 * exactly — the complete one fills it and lays the check over the top, which a
 * stroked check-in-circle glyph can't do without shrinking the disc by its own
 * stroke.
 */
const CompletionIndicator = React.forwardRef<
  HTMLSpanElement,
  CompletionIndicatorProps
>(({ complete, label, className, ...props }, ref) => (
  <span
    ref={ref}
    role="img"
    aria-label={complete ? `${label} selected` : `${label} not selected yet`}
    className={cn(
      "relative grid size-4.5 shrink-0 place-items-center",
      className,
    )}
    {...props}
  >
    {complete ? (
      <>
        <Circle className="size-4.5 fill-coval-success-border text-coval-success-border" />
        <Check
          className="absolute size-3 text-surface-default"
          strokeWidth={3.5}
        />
      </>
    ) : (
      <Circle className="size-4.5 text-coval-border" />
    )}
  </span>
))

CompletionIndicator.displayName = "CompletionIndicator"

export { CompletionIndicator }
